import { getActiveRuneWords } from './runewords';
import { DROP_POOL_EXPANDED } from './dropPoolExpanded';
import { DROP_POOL_HIGH_LEVEL } from './dropPoolHighLevel';
import { DROP_POOL_SURVIVABILITY } from './dropPoolSurvivability';
import { SET_ITEM_POOL, getActiveSetBonuses } from './sets';

// ─── Weapon attack profiles ───────────────────────────────────────────────────
// Defines the visual + mechanical attack shape per weapon type.
// radiusFactor scales ATTACK_RADIUS; duration is the hitbox active window (ms);
// baseCd is the base attack cooldown in ms (then multiplied by state.cdMult).
export const WEAPON_ATTACK_PROFILES = {
  unarmed: { shape: 'circle', radiusFactor: 1.00, duration: 400, baseCd: 1000, cssClass: '' },
  sword:   { shape: 'circle', radiusFactor: 1.00, duration: 380, baseCd:  950, cssClass: 'attack--sword' },
  dagger:  { shape: 'circle', radiusFactor: 0.72, duration: 200, baseCd:  600, cssClass: 'attack--dagger' },
  trident: { shape: 'wide',   radiusFactor: 1.25, duration: 480, baseCd: 1200, cssClass: 'attack--trident' },
  scythe:  { shape: 'circle', radiusFactor: 1.35, duration: 450, baseCd:  950, cssClass: 'attack--scythe' },
  // ── New weapon types ─────────────────────────────────────────────────────
  hammer:  { shape: 'circle', radiusFactor: 1.10, duration: 500, baseCd: 1500, cssClass: 'attack--hammer' },
  staff:   { shape: 'circle', radiusFactor: 1.50, duration: 600, baseCd: 1100, cssClass: 'attack--staff'  },
  axe:     { shape: 'circle', radiusFactor: 0.95, duration: 300, baseCd:  700, cssClass: 'attack--axe'    },
  katana:  { shape: 'wide',   radiusFactor: 1.15, duration: 350, baseCd:  650, cssClass: 'attack--katana' },
};

// ─── Rarity colours ───────────────────────────────────────────────────────────
export const RARITY_COLORS = {
  common:    '#9e9e9e',
  uncommon:  '#4caf50',
  rare:      '#2196f3',
  set:       '#00e676',
  epic:      '#9c27b0',
  legendary: '#ff9800',
};

// ─── Sell prices by rarity ────────────────────────────────────────────────────
export const SELL_PRICES = {
  common:    5,
  uncommon:  15,
  rare:      40,
  set:       150,
  epic:      100,
  legendary: 300,
};

export function getSellPrice(item) {
  return SELL_PRICES[item?.rarity] ?? 5;
}

// ─── Item factory (module-level counter → stable IDs on every load) ───────────
let _nextId = 1;
function mkItem(slotType, icon, name, rarity, statMods, weaponType, levelReq = 1) {
  return {
    id: `item_${_nextId++}`, slotType, icon, name, rarity, statMods, levelReq,
    ...(weaponType && { weaponType }),
  };
}

// ─── Item catalogue (starter inventory) ───────────────────────────────────────
// levelReq: minimum player (meta) level to equip the item.
// common=1, uncommon=5, rare=10, epic=15 for starter items.
export const ITEM_CATALOGUE = [
  // Weapons
  mkItem('weapon',  '⚔️',  'Rusty Sword',       'common',    { attackDamage: 1 },                                     'sword',   1),
  mkItem('weapon',  '⚔️',  'Iron Blade',         'uncommon',  { attackDamage: 2 },                                     'sword',   5),
  mkItem('weapon',  '🗡️',  'Speed Dagger',       'rare',      { attackDamage: 1, speedMult: 0.15, critRate: 0.08 },    'dagger',  10),
  mkItem('weapon',  '🔱',  'War Trident',        'epic',      { attackDamage: 3, rangeMult: 0.1,  critRate: 0.05 },    'trident', 15),
  // Helmets
  mkItem('helmet',  '🪖',  'Leather Cap',        'common',    { speedMult: 0.1  },                                     undefined, 1),
  mkItem('helmet',  '🪖',  'Iron Helm',          'uncommon',  { speedMult: 0.15, cdMult: -0.05 },                      undefined, 5),
  mkItem('helmet',  '🎓',  "Scholar's Hood",     'rare',      { xpPerKill: 1    },                                     undefined, 10),
  // Armor
  mkItem('armor',   '🛡️',  'Padded Vest',        'common',    { enemySlowMult: -0.1, armor: 2 },                       undefined, 1),
  mkItem('armor',   '🛡️',  'Chain Mail',         'uncommon',  { cdMult: -0.15, armor: 3 },                             undefined, 5),
  mkItem('armor',   '🪬',  'Battle Plate',       'rare',      { attackDamage: 2, enemySlowMult: -0.1, armor: 5 },      undefined, 10),
  // Rings (slotType 'ring' → equips to ring1 or ring2)
  mkItem('ring',    '💍',  'Swift Ring',         'common',    { speedMult: 0.15 },                                     undefined, 1),
  mkItem('ring',    '💍',  'Power Ring',         'uncommon',  { attackDamage: 1 },                                     undefined, 5),
  mkItem('ring',    '💎',  'Scholar Ring',       'rare',      { xpPerKill: 2    },                                     undefined, 10),
  mkItem('ring',    '💍',  'Reach Ring',         'uncommon',  { rangeMult: 0.15 },                                     undefined, 6),
  // Gloves
  mkItem('gloves',  '🧤',  'Worn Gloves',        'common',    { cdMult: -0.1  },                                       undefined, 1),
  mkItem('gloves',  '🧤',  'Battle Mitts',       'uncommon',  { attackDamage: 1, cdMult: -0.1 },                       undefined, 5),
  // Boots
  mkItem('boots',   '👟',  'Fleet Boots',        'common',    { speedMult: 0.2  },                                     undefined, 1),
  mkItem('boots',   '👢',  "Sprinter's Boots",   'uncommon',  { speedMult: 0.3  },                                     undefined, 6),
  // Amulets
  mkItem('amulet',  '📿',  'Amulet of Range',    'common',    { rangeMult: 0.2, fireRes: 0.10 },                       undefined, 1),
  mkItem('amulet',  '🔮',  'Grand Amulet',       'rare',      { rangeMult: 0.2, attackDamage: 1, lightningRes: 0.10 }, undefined, 10),
];

// ─── Rune factory ────────────────────────────────────────────────────────────
// Runes have slotType 'rune', exactly one stat mod, and a rarity derived from
// their level requirement:  5–24 → common · 25–44 → uncommon · 45–64 → rare
//                          65–84 → epic · 85–100 → legendary
function runeRarity(levelReq) {
  if (levelReq >= 85) return 'legendary';
  if (levelReq >= 65) return 'epic';
  if (levelReq >= 45) return 'rare';
  if (levelReq >= 25) return 'uncommon';
  return 'common';
}
function mkRune(name, levelReq, statMods) {
  const rarity = runeRarity(levelReq);
  return { id: `rune_${_nextId++}`, slotType: 'rune', icon: '🔮', name, rarity, levelReq, statMods };
}

// ─── 50 Runes ─────────────────────────────────────────────────────────────────
// Distributed across levelReq 5–100 in steps of 2; rarity scales with level.
// Each rune grants exactly ONE stat bonus that grows with its level requirement.
export const RUNE_CATALOGUE = [
  // ── Common (lv 5–23) ───────────────────────────────────────────────────────
  mkRune('Rune of Striking',     5,  { attackDamage: 0.5  }),
  mkRune('Rune of Speed',        7,  { speedMult: 0.05    }),
  mkRune('Rune of Endurance',    9,  { armor: 1           }),
  mkRune('Rune of Aggression',   11, { attackDamage: 1    }),
  mkRune('Rune of Swiftness',    13, { speedMult: 0.08    }),
  mkRune('Rune of Focus',        15, { critRate: 0.02     }),
  mkRune('Rune of Protection',   17, { armor: 2           }),
  mkRune('Rune of Alacrity',     19, { cdMult: -0.05      }),
  mkRune('Rune of Power',        21, { attackDamage: 1.5  }),
  mkRune('Rune of Warding',      23, { armor: 3           }),
  // ── Uncommon (lv 25–43) ────────────────────────────────────────────────────
  mkRune('Rune of Precision',    25, { critRate: 0.04     }),
  mkRune('Rune of Velocity',     27, { speedMult: 0.12    }),
  mkRune('Rune of Impact',       29, { attackDamage: 2    }),
  mkRune('Rune of Resilience',   31, { armor: 4           }),
  mkRune('Rune of Dispatch',     33, { cdMult: -0.10      }),
  mkRune('Rune of Reach',        35, { rangeMult: 0.10    }),
  mkRune('Rune of Brutality',    37, { attackDamage: 2.5  }),
  mkRune('Rune of Insight',      39, { xpPerKill: 1       }),
  mkRune('Rune of Cunning',      41, { critDamage: 0.10   }),
  mkRune('Rune of Flame',        43, { fireRes: 0.08      }),
  // ── Rare (lv 45–63) ────────────────────────────────────────────────────────
  mkRune('Rune of Momentum',     45, { speedMult: 0.18    }),
  mkRune('Rune of Dominance',    47, { attackDamage: 3    }),
  mkRune('Rune of Deadliness',   49, { critRate: 0.08     }),
  mkRune('Rune of Fortitude',    51, { armor: 6           }),
  mkRune('Rune of Haste',        53, { cdMult: -0.15      }),
  mkRune('Rune of Mastery',      55, { attackDamage: 3.5  }),
  mkRune('Rune of Decimation',   57, { critDamage: 0.20   }),
  mkRune('Rune of Expansion',    59, { rangeMult: 0.20    }),
  mkRune('Rune of Storm',        61, { lightningRes: 0.15 }),
  mkRune('Rune of Erudition',    63, { xpPerKill: 2       }),
  // ── Epic (lv 65–83) ────────────────────────────────────────────────────────
  mkRune('Rune of Carnage',      65, { attackDamage: 4    }),
  mkRune('Rune of Celerity',     67, { speedMult: 0.25    }),
  mkRune('Rune of Devastation',  69, { critRate: 0.12     }),
  mkRune('Rune of Bastion',      71, { armor: 8           }),
  mkRune('Rune of Quickening',   73, { cdMult: -0.20      }),
  mkRune('Rune of Slaughter',    75, { attackDamage: 4.5  }),
  mkRune('Rune of Annihilation', 77, { critDamage: 0.30   }),
  mkRune('Rune of Extension',    79, { rangeMult: 0.30    }),
  mkRune('Rune of Conflagration',81, { fireRes: 0.25      }),
  mkRune('Rune of Enlightenment',83, { xpPerKill: 3       }),
  // ── Legendary (lv 85–100) ──────────────────────────────────────────────────
  mkRune('Rune of Obliteration', 85, { attackDamage: 5    }),
  mkRune('Rune of Transcendence',87, { speedMult: 0.40    }),
  mkRune('Rune of Execution',    89, { critRate: 0.17     }),
  mkRune('Rune of Invulnerability',91,{ armor: 12         }),
  mkRune('Rune of Rapidity',     93, { cdMult: -0.28      }),
  mkRune('Rune of the Conqueror',95, { attackDamage: 5.5  }),
  mkRune('Rune of Lethal Edge',  97, { critDamage: 0.40   }),
  mkRune('Rune of Omnipotence',  98, { rangeMult: 0.45    }),
  mkRune('Rune of the Void',     99, { attackDamage: 6    }),
  mkRune('Rune of Eternity',    100, { critRate: 0.20     }),
];

// ─── Starter inventory: all catalogue items + first 5 runes + padding to 400 ─
export const STARTER_INVENTORY = [
  ...ITEM_CATALOGUE,
  ...RUNE_CATALOGUE.slice(0, 5),
  ...Array(400 - ITEM_CATALOGUE.length - 5).fill(null),
];

// ─── Empty equipped set ───────────────────────────────────────────────────────
export const EMPTY_EQUIPPED = {
  helmet: null,
  weapon: null,
  armor:  null,
  ring1:  null,
  ring2:  null,
  gloves: null,
  boots:  null,
  amulet: null,
  // ─ Per-slot rune sockets (one per equipment slot)
  helmet_rune: null,
  weapon_rune: null,
  armor_rune:  null,
  ring1_rune:  null,
  ring2_rune:  null,
  gloves_rune: null,
  boots_rune:  null,
  amulet_rune: null,
};

// ─── Legendary ability definitions ────────────────────────────────────────────
// Items only store an `abilityId` string (JSON-safe for localStorage).
// This map holds the full runtime objects with their applyToState functions.
export const LEGENDARY_ABILITIES = {
  soul_drain: {
    id:   'soul_drain',
    name: '🩸 Soul Drain',
    desc: 'Every 10 kills, restore 20 HP (up to max)',
    applyToState(state) {
      state.soulDrainActive  = true;
      state.soulDrainCounter = 0;
    },
  },
  undying_will: {
    id:   'undying_will',
    name: '♾️ Undying Will',
    desc: 'Start with 150 HP; max HP raised to 200',
    applyToState(state) {
      state.maxHp = 200;
      state.hp    = 150;
    },
  },
  void_pulse: {
    id:   'void_pulse',
    name: '💥 Void Pulse',
    desc: 'On taking a hit, blast all enemies 150 px away',
    applyToState(state) {
      state.voidPulseActive = true;
    },
  },
  blood_pact: {
    id:   'blood_pact',
    name: '🔥 Blood Pact',
    desc: 'Melee deals ×2 damage — but lose 20 HP on each level-up',
    applyToState(state) {
      state.lichPactActive = true;
      state.damageMult     = 2;
    },
  },
  bloodlust: {
    id:   'bloodlust',
    name: '⚔️ Bloodlust',
    desc: 'Each melee kill permanently adds +0.1 attack damage this run',
    applyToState(state) {
      state.bloodlustActive = true;
    },
  },
  ghost_step: {
    id:   'ghost_step',
    name: '👻 Ghost Step',
    desc: 'Auto-teleports you to center every 20 seconds',
    applyToState(state) {
      state.phantomStrideActive = true;
      state.phantomStrideAt     = 0; // resolved to timestamp+20000 on first loop frame
    },
  },
  death_nova: {
    id:   'death_nova',
    name: '☠️ Death Nova',
    desc: 'On losing a life, destroy all enemies within 250 px',
    applyToState(state) {
      state.deathNovaActive = true;
    },
  },
  solar_flare: {
    id:   'solar_flare',
    name: '☀️ Solar Flare',
    desc: 'First attack after each level-up deals ×5 damage',
    applyToState(state) {
      state.solarFlareActive = true;
    },
  },
  chain_reaction: {
    id:   'chain_reaction',
    name: '⚡ Chain Reaction',
    desc: '30% chance each melee kill also slays the nearest enemy',
    applyToState(state) {
      state.stormSurgeActive = true;
    },
  },
  spectral_armor: {
    id:   'spectral_armor',
    name: '🌫️ Spectral Armor',
    desc: 'Invincibility after a hit lasts 3 s instead of 1.5 s',
    applyToState(state) {
      state.hitCooldown = 3000;
    },
  },
  vampiric_strike: {
    id:   'vampiric_strike',
    name: '🩸 Vampiric Strike',
    desc: 'Each kill restores 5 HP (up to max)',
    applyToState(state) {
      state.vampiricActive = true;
    },
  },
  shadow_dodge: {
    id:   'shadow_dodge',
    name: '👻 Shadow Dodge',
    desc: '25% chance to dodge incoming damage completely',
    applyToState(state) {
      state.shadowDodgeChance = 0.25;
    },
  },
  titan_form: {
    id:   'titan_form',
    name: '💥 Titan Form',
    desc: '+30 Armor; max HP raised to 300',
    applyToState(state) {
      state.armor  = (state.armor ?? 0) + 30;
      state.maxHp  = 300;
    },
  },
  endless_hunger: {
    id:   'endless_hunger',
    name: '⚡ Endless Hunger',
    desc: 'Gain 2× XP per kill and +1 gold find multiplier',
    applyToState(state) {
      state.xpPerKill      = (state.xpPerKill ?? 1) * 2;
      state.goldFindBonus  = (state.goldFindBonus ?? 0) + 1;
    },
  },
  glass_cannon: {
    id:   'glass_cannon',
    name: '💀 Glass Cannon',
    desc: 'Triple attack damage — but max HP reduced to 30',
    applyToState(state) {
      state.attackDamage = (state.attackDamage ?? 1) * 3;
      state.maxHp        = 30;
      state.hp           = Math.min(state.hp ?? 30, 30);
    },
  },
  arcane_conduit: {
    id:   'arcane_conduit',
    name: '🔮 Arcane Conduit',
    desc: '+100% attack range — but attacks 50% slower',
    applyToState(state) {
      state.rangeMult = (state.rangeMult ?? 1) + 1.0;
      state.cdMult    = (state.cdMult ?? 1) + 0.50;
    },
  },
  berserker_rage: {
    id:   'berserker_rage',
    name: '⚡ Berserker Rage',
    desc: 'Attack damage increases by +0.2 for every 10% HP missing',
    applyToState(state) {
      state.berserkerActive = true;
    },
  },
  mirror_image: {
    id:   'mirror_image',
    name: '🌀 Mirror Image',
    desc: '20% chance every kill triggers an identical free attack',
    applyToState(state) {
      state.mirrorImageActive = true;
    },
  },
};

// ─── Drop pool: templates (no IDs) for procedurally generated loot ────────────
// levelReq: minimum player (meta) level to equip — also controls which enemy tiers
// can drop this item (enemies drop items within ±10 of their level = tierIdx * 2).
// Bands: common 1–5 · uncommon 5–9 · rare 10–14 · epic 15–19 · legendary 20 (fixed)
const DROP_POOL = [
  // ── Weapons ───────────────────────────────────────────────────────────────
  { slotType: 'weapon', icon: '⚔️', name: 'Rusty Sword',       rarity: 'common',    levelReq: 1,  weaponType: 'sword',   statMods: { attackDamage: 1 } },
  { slotType: 'weapon', icon: '⚔️', name: 'Iron Blade',         rarity: 'uncommon',  levelReq: 5,  weaponType: 'sword',   statMods: { attackDamage: 2 } },
  { slotType: 'weapon', icon: '🗡️', name: 'Speed Dagger',       rarity: 'rare',      levelReq: 10, weaponType: 'dagger',  statMods: { attackDamage: 1, speedMult: 0.15, critRate: 0.08 } },
  { slotType: 'weapon', icon: '🔱', name: 'War Trident',        rarity: 'epic',      levelReq: 15, weaponType: 'trident', statMods: { attackDamage: 3, rangeMult: 0.1,  critRate: 0.05 } },
  {
    slotType: 'weapon', icon: '🩸', name: "Soulreaper's Edge",  rarity: 'legendary', levelReq: 20,
    weaponType: 'scythe',
    abilityId: 'soul_drain',
    statMods: { attackDamage: 3, rangeMult: 0.15, critRate: 0.10, critDamage: 0.25 },
  },
  // ── Helmets ───────────────────────────────────────────────────────────────
  { slotType: 'helmet', icon: '🪖', name: 'Leather Cap',        rarity: 'common',    levelReq: 1,  statMods: { speedMult: 0.1 } },
  { slotType: 'helmet', icon: '🪖', name: 'Iron Helm',          rarity: 'uncommon',  levelReq: 5,  statMods: { speedMult: 0.15, cdMult: -0.05 } },
  { slotType: 'helmet', icon: '🎓', name: "Scholar's Hood",     rarity: 'rare',      levelReq: 10, statMods: { xpPerKill: 1 } },
  { slotType: 'helmet', icon: '🔮', name: 'Mystic Circlet',     rarity: 'epic',      levelReq: 15, statMods: { speedMult: 0.25, xpPerKill: 2 } },
  {
    slotType: 'helmet', icon: '👑', name: 'Crown of Eternity',  rarity: 'legendary', levelReq: 20,
    abilityId: 'undying_will',
    statMods: { speedMult: 0.20, xpPerKill: 2 },
  },
  {
    slotType: 'helmet', icon: '☀️', name: 'Dawnbreaker Helm',   rarity: 'legendary', levelReq: 20,
    abilityId: 'solar_flare',
    statMods: { speedMult: 0.20, xpPerKill: 1 },
  },
  // ── Armor ─────────────────────────────────────────────────────────────────
  { slotType: 'armor',  icon: '🛡️', name: 'Padded Vest',        rarity: 'common',    levelReq: 2,  statMods: { enemySlowMult: -0.1, armor: 2 } },
  { slotType: 'armor',  icon: '🛡️', name: 'Chain Mail',         rarity: 'uncommon',  levelReq: 6,  statMods: { cdMult: -0.15, armor: 3 } },
  { slotType: 'armor',  icon: '🪬', name: 'Battle Plate',       rarity: 'rare',      levelReq: 11, statMods: { attackDamage: 2, enemySlowMult: -0.1, armor: 5 } },
  { slotType: 'armor',  icon: '🏆', name: "Champion's Plate",   rarity: 'epic',      levelReq: 16, statMods: { attackDamage: 2, cdMult: -0.2, enemySlowMult: -0.15, armor: 8 } },
  {
    slotType: 'armor',  icon: '🌑', name: 'Voidplate',           rarity: 'legendary', levelReq: 20,
    abilityId: 'void_pulse',
    statMods: { attackDamage: 2, enemySlowMult: -0.20 },
  },
  {
    slotType: 'armor',  icon: '🌫️', name: 'Wraithshroud',        rarity: 'legendary', levelReq: 20,
    abilityId: 'spectral_armor',
    statMods: { speedMult: 0.20, enemySlowMult: -0.15 },
  },
  // ── Rings ─────────────────────────────────────────────────────────────────
  { slotType: 'ring',   icon: '💍', name: 'Swift Ring',          rarity: 'common',    levelReq: 1,  statMods: { speedMult: 0.15 } },
  { slotType: 'ring',   icon: '💍', name: 'Power Ring',          rarity: 'uncommon',  levelReq: 5,  statMods: { attackDamage: 1 } },
  { slotType: 'ring',   icon: '💍', name: 'Reach Ring',          rarity: 'uncommon',  levelReq: 6,  statMods: { rangeMult: 0.15 } },
  { slotType: 'ring',   icon: '💎', name: 'Scholar Ring',        rarity: 'rare',      levelReq: 11, statMods: { xpPerKill: 2 } },
  {
    slotType: 'ring',   icon: '💀', name: "Lich King's Signet",  rarity: 'legendary', levelReq: 20,
    abilityId: 'blood_pact',
    statMods: { attackDamage: 3, cdMult: -0.25 },
  },
  {
    slotType: 'ring',   icon: '🌪️', name: 'Stormsurge Band',     rarity: 'legendary', levelReq: 20,
    abilityId: 'chain_reaction',
    statMods: { attackDamage: 2, rangeMult: 0.20 },
  },
  // ── Gloves ────────────────────────────────────────────────────────────────
  { slotType: 'gloves', icon: '🧤', name: 'Worn Gloves',         rarity: 'common',    levelReq: 2,  statMods: { cdMult: -0.1 } },
  { slotType: 'gloves', icon: '🧤', name: 'Battle Mitts',        rarity: 'uncommon',  levelReq: 6,  statMods: { attackDamage: 1, cdMult: -0.1 } },
  {
    slotType: 'gloves', icon: '⚒️', name: "Titan's War Gauntlets", rarity: 'legendary', levelReq: 20,
    abilityId: 'bloodlust',
    statMods: { attackDamage: 2, cdMult: -0.15 },
  },
  // ── Boots ─────────────────────────────────────────────────────────────────
  { slotType: 'boots',  icon: '👟', name: 'Fleet Boots',          rarity: 'common',    levelReq: 1,  statMods: { speedMult: 0.2 } },
  { slotType: 'boots',  icon: '👢', name: "Sprinter's Boots",     rarity: 'uncommon',  levelReq: 6,  statMods: { speedMult: 0.3 } },
  { slotType: 'boots',  icon: '💠', name: 'Phase Boots',          rarity: 'epic',      levelReq: 17, statMods: { speedMult: 0.5 } },
  {
    slotType: 'boots',  icon: '👻', name: 'Phantom Stride Boots', rarity: 'legendary', levelReq: 20,
    abilityId: 'ghost_step',
    statMods: { speedMult: 0.50 },
  },
  // ── Amulets ───────────────────────────────────────────────────────────────
  { slotType: 'amulet', icon: '📿', name: 'Amulet of Range',      rarity: 'common',    levelReq: 2,  statMods: { rangeMult: 0.2, fireRes: 0.10 } },
  { slotType: 'amulet', icon: '🔮', name: 'Grand Amulet',         rarity: 'rare',      levelReq: 12, statMods: { rangeMult: 0.2, attackDamage: 1, lightningRes: 0.10 } },
  { slotType: 'amulet', icon: '🌙', name: 'Moonstone Amulet',     rarity: 'epic',      levelReq: 17, statMods: { rangeMult: 0.3, attackDamage: 2, fireRes: 0.15, lightningRes: 0.10 } },
  {
    slotType: 'amulet', icon: '☠️', name: 'Amulet of Ruin',       rarity: 'legendary', levelReq: 20,
    abilityId: 'death_nova',
    statMods: { attackDamage: 3, rangeMult: 0.30, fireRes: 0.20 },
  },
  // ── Pure resist / armor drops ─────────────────────────────────────────────
  { slotType: 'amulet', icon: '🌋', name: 'Fire Pendant',          rarity: 'uncommon',  levelReq: 5,  statMods: { fireRes: 0.15 } },
  { slotType: 'amulet', icon: '🌩️', name: 'Storm Talisman',        rarity: 'rare',      levelReq: 8,  statMods: { lightningRes: 0.20 } },
  { slotType: 'ring',   icon: '🔩', name: 'Iron Ring',             rarity: 'common',    levelReq: 3,  statMods: { armor: 3 } },
  { slotType: 'ring',   icon: '🗡️', name: "Assassin's Band",       rarity: 'rare',      levelReq: 13, statMods: { critRate: 0.10, critDamage: 0.20 } },
  { slotType: 'gloves', icon: '⚔️', name: "Duelist's Gloves",      rarity: 'rare',      levelReq: 12, statMods: { critRate: 0.08, critDamage: 0.15, cdMult: -0.05 } },
  { slotType: 'armor',  icon: '🧊', name: 'Frosted Chainmail',     rarity: 'rare',      levelReq: 11, statMods: { armor: 4, lightningRes: 0.12 } },
  { slotType: 'armor',  icon: '🌋', name: 'Flame-Hardened Plate',  rarity: 'epic',      levelReq: 18, statMods: { armor: 7, fireRes: 0.18 } },
  ...DROP_POOL_EXPANDED,
  ...DROP_POOL_HIGH_LEVEL,
  ...DROP_POOL_SURVIVABILITY,
  ...SET_ITEM_POOL,
];

// ─── Endgame drop pool (tier 100+) ───────────────────────────────────────────
// After tier 100 only high-level items and set items drop from enemies.
// Runes come from RUNE_CATALOGUE via rollRuneDrop (unchanged).
const DROP_POOL_ENDGAME = [
  ...DROP_POOL_HIGH_LEVEL,
  ...SET_ITEM_POOL,
];

// ─── Rarity picker (weighted): 58/30/8/2/1.8/0.2 ────────────────────────────
// magicFind (0–2.0) shifts probability mass from common toward higher rarities.
// At MF=1.0 (~100%): common≈46%, uncommon≈32%, rare≈12%, set≈3%, epic≈5%, legendary≈2%.
// At MF=2.0 (~200%): common≈34%, uncommon≈34%, rare≈16%, set≈4%, epic≈9%, legendary≈3%.
function pickRarity(magicFind = 0) {
  const mf      = Math.min(magicFind, 2.0);
  const common  = 58 - mf * 12;
  const uncommon = 30 + mf * 2;
  const rare     = 8  + mf * 4;
  const set      = 2  + mf * 1;
  const epic     = 1.8 + mf * 3.7;
  // legendary gets the remainder up to 100
  const r = Math.random() * 100;
  if (r < common)                              return 'common';
  if (r < common + uncommon)                   return 'uncommon';
  if (r < common + uncommon + rare)            return 'rare';
  if (r < common + uncommon + rare + set)      return 'set';
  if (r < common + uncommon + rare + set + epic) return 'epic';
  return 'legendary';
}

let _dropSeq = 0;

/**
 * Returns the level window half-width for drop filtering.
 * Tier 1–100  (playerLevel ≤ 100): ±25
 * Tier 101+   (playerLevel  > 100): ±50
 */
function dropLevelWindow(playerLevel) {
  return playerLevel > 100 ? 50 : 25;
}

/**
 * Roll for an item drop.
 * @param {number} extraChance  Additional base drop probability (0–1) from world modifiers.
 * @param {number} playerLevel  Effective player level = score / 1000. Items within the level
 *                              window are eligible: ±25 for levels 1–100, ±50 for 101+.
 * @param {number} magicFind    Magic find value (0–2.0).
 * @param {number} currentTier  Current game tier. At tier 100+ only DROP_POOL_HIGH_LEVEL and
 *                              SET_ITEM_POOL are used so only relevant endgame items drop.
 * Returns a fresh item instance (unique ID) or null.
 * Rarity: common 60% · uncommon 30% · rare 8% · epic 1.8% · legendary 0.2%
 */
export function rollDrop(extraChance = 0, playerLevel = 0, magicFind = 0, currentTier = 0) {
  // MF adds up to +1% absolute base chance at 200 MF (50% boost)
  if (Math.random() >= (0.01 + extraChance + magicFind * 0.005)) return null;
  const rarity = pickRarity(magicFind);
  // Tier 100+: restrict to high-level and set items only
  const pool = currentTier >= 100 ? DROP_POOL_ENDGAME : DROP_POOL;
  const allByRarity = pool.filter(t => t.rarity === rarity);
  const window = dropLevelWindow(playerLevel);
  const levelFiltered = allByRarity.filter(t => Math.abs((t.levelReq ?? 1) - playerLevel) <= window);
  // Fall back to the full rarity pool if the level window yields nothing
  const source = levelFiltered.length > 0 ? levelFiltered : allByRarity;
  if (!source.length) return null;
  const tmpl = source[Math.floor(Math.random() * source.length)];
  return { ...tmpl, id: `drop_${Date.now()}_${_dropSeq++}` };
}

// ─── Chest loot helper ────────────────────────────────────────────────────────
/**
 * Guaranteed item drop for chest loot — skips the probability roll.
 * @param {number} playerLevel  Effective player level = score / 1000.
 * Returns a fresh item instance (unique ID). Never returns null.
 */
export function rollChestItem(playerLevel = 0) {
  const rarity = pickRarity();
  const allByRarity = DROP_POOL.filter(t => t.rarity === rarity);
  const window = dropLevelWindow(playerLevel);
  const levelFiltered = allByRarity.filter(t => Math.abs((t.levelReq ?? 1) - playerLevel) <= window);
  const source = levelFiltered.length > 0 ? levelFiltered : allByRarity;
  const tmpl = source[Math.floor(Math.random() * source.length)] ?? DROP_POOL[0];
  return { ...tmpl, id: `chest_${Date.now()}_${_dropSeq++}` };
}

// ─── Gold drop helper ─────────────────────────────────────────────────────────
/**
 * Returns an amount of gold for a killed enemy.
 *  - Regular enemies: 10 % base chance, 1–2 gold
 *  - Bosses: always drop 5–10 gold
 * goldFindBonus (e.g. 0.25 per shop level) multiplies the result.
 */
export function rollGold(isBoss = false, goldFindBonus = 0) {
  if (!isBoss && Math.random() >= 0.10) return 0;
  const base = isBoss
    ? 5 + Math.floor(Math.random() * 6)   // 5–10
    : 1 + Math.floor(Math.random() * 2);  // 1–2
  return Math.max(1, Math.round(base * (1 + goldFindBonus)));
}

// ─── Rune drop helper ─────────────────────────────────────────────────────────
/**
 * Roll for a rune drop.
 * Base chance: 0.5% per kill. Same rarity weights as item drops.
 * Level filtering: rune's levelReq must be within ±10 of enemyLevel * 5
 * (rune levels go up to 100, enemy levels go up to ~20 for current tiers,
 * so we scale: runeLevel filter = enemyLevel * 5, window ±15).
 * Falls back to full rarity pool if level window is empty.
 */
let _runeDropSeq = 0;
export function rollRuneDrop(extraChance = 0, enemyLevel = 0, magicFind = 0) {
  if (Math.random() >= (0.005 + extraChance * 0.5 + magicFind * 0.003)) return null;
  const rarity = pickRarity(magicFind);
  const allByRarity = RUNE_CATALOGUE.filter(r => r.rarity === rarity);
  // Scale enemy level to rune level space (runes: lv 5–100, enemies: lv 0–20+)
  const runeLevel = enemyLevel * 5;
  const levelFiltered = allByRarity.filter(r => Math.abs(r.levelReq - runeLevel) <= 15);
  const source = levelFiltered.length > 0 ? levelFiltered : allByRarity;
  if (!source.length) return null;
  const tmpl = source[Math.floor(Math.random() * source.length)];
  return { ...tmpl, id: `rune_drop_${Date.now()}_${_runeDropSeq++}` };
}

// ─── Shared helper: apply a stat-mods object to state ────────────────────────
function applyMods(state, m) {
  if (!m) return;
  if (m.speedMult)     state.speedMult     += m.speedMult;
  if (m.rangeMult)     state.rangeMult     += m.rangeMult;
  if (m.cdMult)        state.cdMult        += m.cdMult;
  if (m.attackDamage)  state.attackDamage  += m.attackDamage;
  if (m.xpPerKill)     state.xpPerKill     += m.xpPerKill;
  if (m.enemySlowMult) state.enemySlowMult += m.enemySlowMult;
  if (m.armor)         state.armor          = (state.armor ?? 0) + m.armor;
  if (m.fireRes)       state.fireRes        = Math.min(0.75, (state.fireRes ?? 0) + m.fireRes);
  if (m.lightningRes)  state.lightningRes   = Math.min(0.75, (state.lightningRes ?? 0) + m.lightningRes);
  if (m.critRate)      state.critRate       = Math.min(0.75, (state.critRate ?? 0.05) + m.critRate);
  if (m.critDamage)    state.critDamage     = (state.critDamage ?? 1.5) + m.critDamage;
  if (m.maxHp)         state.maxHp          = (state.maxHp ?? 100) + m.maxHp;
  if (m.dodgeChance)   state.dodgeChance    = Math.min(0.75, (state.dodgeChance ?? 0) + m.dodgeChance);
  if (m.magicFind)      state.magicFind      = (state.magicFind ?? 0) + m.magicFind;
  if (m.goldFindBonus)  state.goldFindBonus  = (state.goldFindBonus ?? 0) + m.goldFindBonus;
}

// ─── Apply equipped items to a mutable game-state object ─────────────────────
export function applyEquippedItems(state, equipped) {
  // Apply weapon type for attack-shape system (unarmed if no weapon)
  state.weaponType = equipped.weapon?.weaponType ?? 'unarmed';

  Object.values(equipped).forEach(item => {
    if (!item) return;
    // Apply base stat mods
    applyMods(state, item.statMods);
    // Apply enhancement stats (stacked on top of base stats)
    if (item.enhancement?.stats) applyMods(state, item.enhancement.stats);
    // Apply legendary passive ability (looks up function by abilityId — survives JSON round-trip)
    if (item.abilityId) {
      const ability = LEGENDARY_ABILITIES[item.abilityId];
      if (ability?.applyToState) ability.applyToState(state);
    }
  });

  // Apply active rune word set bonuses
  for (const word of getActiveRuneWords(equipped)) {
    applyMods(state, word.statMods);
  }

  // Apply active set item bonuses
  for (const bonus of getActiveSetBonuses(equipped)) {
    for (const b of bonus.activeBonuses) applyMods(state, b.statMods);
  }

  // Ensure player starts at full HP after maxHp is boosted by equipment
  if (state.maxHp > 100) state.hp = state.maxHp;

  return state;
}

// ─── Compute effective stats for the CharacterScreen display ─────────────────
export function computeEffectiveStats(equipped) {
  const eff = {
    speedMult:     1,
    rangeMult:     1,
    cdMult:        1,
    attackDamage:  1,
    xpPerKill:     1,
    enemySlowMult: 1,
    armor:         0,
    fireRes:       0,
    lightningRes:  0,
    critRate:      0.05,
    critDamage:    1.5,
    maxHp:         100,
    dodgeChance:   0,
    magicFind:     0,
    goldFindBonus: 0,
  };
  Object.values(equipped).forEach(item => {
    if (!item) return;
    applyMods(eff, item.statMods);
    if (item.enhancement?.stats) applyMods(eff, item.enhancement.stats);
  });

  // Apply active rune word set bonuses
  for (const word of getActiveRuneWords(equipped)) {
    applyMods(eff, word.statMods);
  }

  // Apply active set item bonuses
  for (const bonus of getActiveSetBonuses(equipped)) {
    for (const b of bonus.activeBonuses) applyMods(eff, b.statMods);
  }

  return eff;
}

// ─── Format stat mods into human-readable strings ────────────────────────────
export function formatStatMods(statMods) {
  const lines = [];
  if (statMods.speedMult)     lines.push(`+${Math.round(statMods.speedMult * 100)}% Move Speed`);
  if (statMods.rangeMult)     lines.push(`+${Math.round(statMods.rangeMult * 100)}% Attack Range`);
  if (statMods.cdMult) {
    const pct = Math.round(Math.abs(statMods.cdMult) * 100);
    lines.push(statMods.cdMult < 0 ? `${pct}% Faster Attack` : `+${pct}% Slower Attack`);
  }
  if (statMods.attackDamage)  lines.push(`+${+statMods.attackDamage.toFixed(1)} Attack Damage`);
  if (statMods.xpPerKill)     lines.push(`+${statMods.xpPerKill} XP per Kill`);
  if (statMods.enemySlowMult) {
    const pct = Math.round(Math.abs(statMods.enemySlowMult) * 100);
    lines.push(statMods.enemySlowMult < 0 ? `Enemies ${pct}% Slower` : `Enemies ${pct}% Faster`);
  }
  if (statMods.armor)        lines.push(`+${statMods.armor} Armor`);
  if (statMods.fireRes)      lines.push(`+${Math.round(statMods.fireRes * 100)}% Fire Resist`);
  if (statMods.lightningRes) lines.push(`+${Math.round(statMods.lightningRes * 100)}% Lightning Resist`);
  if (statMods.critRate)     lines.push(`+${Math.round(statMods.critRate * 100)}% Crit Chance`);
  if (statMods.critDamage)   lines.push(`+${Math.round(statMods.critDamage * 100)}% Crit Damage`);
  if (statMods.maxHp)        lines.push(`+${statMods.maxHp} Max HP`);
  if (statMods.dodgeChance)  lines.push(`+${Math.round(statMods.dodgeChance * 100)}% Dodge Chance`);
  if (statMods.magicFind)      lines.push(`+${Math.round(statMods.magicFind * 100)}% Magic Find`);
  if (statMods.goldFindBonus)  lines.push(`+${Math.round(statMods.goldFindBonus * 100)}% Gold Find`);
  return lines;
}
